import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { useSearchParams, useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Image as ImageIcon,
  Upload,
  X,
  Trash2,
  Calendar as CalendarIcon,
  Clock,
  Loader2,
  Send,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  MoreHorizontal,
  Repeat2,
  BarChart3,
  Globe,
  ThumbsUp,
  Music,
  Plus,
  Check,
  ChevronLeft,
  ChevronRight,
  Volume2,
  Play,
  Search,
  Home,
  Compass,
  PlusSquare,
  User,
  Film,
  Zap,
  Sparkles,
  Wand2,
  Save,
  Lightbulb,
  PenSquare,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  UserCheck,
  Copy,
} from 'lucide-react';
import { FaBluesky, FaPinterest } from 'react-icons/fa6';
import { api, ApiError } from '../lib/api';
import { PLATFORM_META, PLATFORM_CHAR_LIMITS, effectiveCharLimit, type Platform } from '../lib/platforms';
import { useAuth } from '../auth/AuthProvider';
import {
  zonedInputToIso, isoToZonedInput, nowZoned, zonedDateToInput,
  detectTimezone, timezoneLabel,
} from '../lib/timezones';
import { cn } from '../lib/cn';
import { AccountAvatar } from '../components/AccountAvatar';
import { QueueSlotsEditor } from '../components/QueueSlotsEditor';
import { CollaboratorsInput, VideoCoverPicker, type VideoCover } from '../components/PostExtras';
import { bestTimesSourceNote, upcomingBestTimes, useBestTimes } from '../lib/bestTimes';
import { AiPredictionModal } from '../components/AiPredictionModal';
import { AiCaptionModal } from '../components/AiCaptionModal';
import { AiIdeasModal } from '../components/AiIdeasModal';
import { PlatformPreview, XVerifiedBadge } from '../components/platformPreviews';

type PublishMode = 'now' | 'scheduled' | 'queue';

/* ─────────────────────────────────────────────────────────────
   Types & constants
   ─────────────────────────────────────────────────────────── */
type MediaItem = {
  id: string;
  url: string;
  thumbnailUrl: string | null;
  kind: 'image' | 'video';
  mimeType: string;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  status: 'pending' | 'ready' | 'failed';
  originalName: string | null;
  createdAt: string;
};

type ConnectedAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  status: 'connected' | 'disconnected';
  isPremium: boolean;
};

type OkMsgTarget = {
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
};

type OkMsg = {
  mode: 'now' | 'scheduled' | 'queue';
  scheduledAtIso: string | null;
  targets: OkMsgTarget[];
};

export type PostKind =
  | 'post'
  | 'reel'
  | 'story'
  | 'video'
  | 'short'
  | 'pin'
  | 'photo';

/**
 * One destination for a post.
 * - `caption`: optional per-network override. When `null` the network uses the
 *   global `copy` (single caption shared across all targets). When set, this
 *   network is published with this text instead — letting the user adapt tone
 *   to each platform (concise + hashtags for IG, longer + professional for
 *   LinkedIn, etc).
 */
type Target = { accountId: string; kind: PostKind; caption: string | null };

const SUPPORTED_KINDS: Record<Platform, PostKind[]> = {
  instagram:       ['post', 'reel', 'story'],
  facebook:        ['post', 'reel', 'story'],
  twitter:         ['post'],
  linkedin:        ['post'],
  tiktok:          ['video', 'photo'],
  tiktok_business: ['video', 'photo'],
  youtube:         ['short', 'video'],
  pinterest:       ['pin'],
  threads:         ['post'],
  bluesky:         ['post'],
};

const KIND_LABEL: Record<PostKind, string> = {
  post:  'Post',
  reel:  'Reel',
  story: 'Story',
  video: 'Vídeo',
  short: 'Short',
  pin:   'Pin',
  photo: 'Foto / Slides',
};

/** Kinds where copy is irrelevant (mostly stories). */
const KIND_HAS_NO_COPY = new Set<PostKind>(['story']);

/** Platforms that can publish a plain-text `post` without any media attached. */
const TEXT_ONLY_PLATFORMS = new Set<Platform>(['twitter', 'linkedin', 'threads', 'bluesky', 'facebook']);

/**
 * Whether a target (platform + kind) can be published with no media.
 * Only plain `post` kinds on text-first networks qualify — reels, stories,
 * videos, pins and Instagram feed posts always need at least one file.
 */
function targetAllowsTextOnly(platform: Platform, kind: PostKind): boolean {
  return kind === 'post' && TEXT_ONLY_PLATFORMS.has(platform);
}

/* ─────────────────────────────────────────────────────────────
   Main page
   ─────────────────────────────────────────────────────────── */
export function PostCreate() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tz = user?.timezone ?? detectTimezone();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // When the route is /dashboard/posts/:id/edit we run in "edit mode": the
  // form pre-fills from the existing post and Save calls PUT instead of POST.
  const { id: editId } = useParams<{ id: string }>();
  const isEditing = Boolean(editId);
  // optional ?at=YYYY-MM-DDTHH:mm preset coming from the calendar
  const presetAt = (() => {
    const at = searchParams.get('at');
    if (at && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(at) && !Number.isNaN(new Date(at).getTime())) {
      return at;
    }
    return null;
  })();

  const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);
  const [targets, setTargets]                   = useState<Target[]>([]);
  const [copy, setCopy]                         = useState('');
  const [publishMode, setPublishMode]           = useState<PublishMode>(presetAt !== null ? 'scheduled' : 'now');
  const [scheduleAt, setScheduleAt]             = useState<string>(presetAt ?? defaultScheduleValue(tz));
  const [previewTargetIdx, setPreviewTargetIdx] = useState(0);
  const [error, setError]                       = useState<string | null>(null);
  const [okMsg, setOkMsg]                       = useState<OkMsg | null>(null);
  const [publishing, setPublishing]             = useState(false);
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [approvalLink, setApprovalLink]         = useState<string | null>(null);
  const [approvalReviewers, setApprovalReviewers] = useState(0);
  const [queueModalOpen, setQueueModalOpen]     = useState(false);
  const [aiModalOpen, setAiModalOpen]           = useState(false);
  const [captionModalOpen, setCaptionModalOpen] = useState(false);
  const [ideasModalOpen, setIdeasModalOpen] = useState(false);
  const [cover, setCover]                       = useState<VideoCover | null>(null);
  const [collaborators, setCollaborators]       = useState<string[]>([]);

  // Auto-dismiss the success banner after 30 s so it doesn't linger forever.
  useEffect(() => {
    if (!okMsg) return;
    const t = setTimeout(() => setOkMsg(null), 30_000);
    return () => clearTimeout(t);
  }, [okMsg]);

  // Prefill from /dashboard/inspiration or /dashboard/sorteos (or any page) that
  // navigates here with location.state.{prefillMediaId, prefillCaption,
  // prefillHashtags, prefillAccountId, prefillStory}.
  useEffect(() => {
    const s = location.state as
      | {
          prefillMediaId?: string;
          prefillCaption?: string;
          prefillHashtags?: string[];
          prefillAccountId?: string;
          prefillStory?: boolean;
        }
      | null;
    if (!s || (!s.prefillMediaId && !s.prefillCaption && !s.prefillAccountId)) return;
    if (s.prefillCaption) {
      const tags = s.prefillHashtags?.length
        ? '\n\n' + s.prefillHashtags.map((t) => `#${t}`).join(' ')
        : '';
      setCopy(s.prefillCaption + tags);
    }
    if (s.prefillMediaId) {
      setSelectedMediaIds([s.prefillMediaId]);
      void queryClient.invalidateQueries({ queryKey: ['media'] });
    }
    // Preselect the account as a target (story mode when requested — e.g. sorteos).
    if (s.prefillAccountId) {
      setTargets([{ accountId: s.prefillAccountId, kind: s.prefillStory ? 'story' : 'post', caption: null }]);
    }
    // Clear the state so a refresh / back-forward doesn't reapply the prefill.
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextSlotQuery = useQuery({
    queryKey: ['queue', 'next'],
    queryFn: () => api<{ scheduledAt: string }>('/api/queue/next'),
    enabled: publishMode === 'queue',
  });

  const mediaQuery = useQuery({
    queryKey: ['media'],
    queryFn: () => api<{ items: MediaItem[] }>('/api/media'),
  });

  const accountsQuery = useQuery({
    queryKey: ['social', 'accounts'],
    queryFn: () => api<{ accounts: ConnectedAccount[] }>('/api/social/accounts'),
  });

  // ── Edit mode: load the post once, then prefill state on first arrival ──
  type EditTarget = {
    socialAccountId: string;
    status: string;
    kind: string;
    caption: string | null;
    collaborators: string[] | null;
    platform: string;
  };
  type EditPost = {
    id: string;
    caption: string | null;
    media: { url: string; type?: string; coverUrl?: string; coverMs?: number }[];
    status: string;
    scheduledAt: string | null;
    targets: EditTarget[];
  };
  const editPostQuery = useQuery({
    queryKey: ['post', editId] as const,
    queryFn: () => api<{ post: EditPost }>(`/api/posts/${editId}`),
    enabled: isEditing,
  });
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (!isEditing || prefilled) return;
    const p = editPostQuery.data?.post;
    const mediaItems = mediaQuery.data?.items;
    if (!p || !mediaItems) return;

    if (p.status !== 'scheduled' && p.status !== 'draft') {
      setError(
        'Esta publicación ya no puede editarse: ya se ha enviado a publicar.',
      );
      setPrefilled(true);
      return;
    }

    // Resolve the post's stored {url, type} list back into media library ids
    // so the existing selectedMediaIds-based UI keeps working unchanged.
    const idByUrl = new Map(mediaItems.map((m) => [m.url, m.id]));
    const ids = p.media.map((m) => idByUrl.get(m.url)).filter((x): x is string => Boolean(x));
    setSelectedMediaIds(ids);

    setCopy(p.caption ?? '');
    setTargets(
      p.targets.map((t) => ({
        accountId: t.socialAccountId,
        kind: t.kind as Target['kind'],
        caption: t.caption,
      })),
    );
    const coveredVideo = p.media.find((m) => m.coverUrl);
    const coveredVideoId = coveredVideo ? idByUrl.get(coveredVideo.url) : undefined;
    if (coveredVideo?.coverUrl && coveredVideoId) {
      setCover({ videoId: coveredVideoId, url: coveredVideo.coverUrl, ms: coveredVideo.coverMs ?? null });
    }
    setCollaborators(p.targets.find((t) => t.collaborators?.length)?.collaborators ?? []);

    // If the post was scheduled for the future, default the schedule mode +
    // value to that instant. If it was a draft, leave the user free to choose.
    if (p.scheduledAt) {
      setPublishMode('scheduled');
      // Convert UTC → the user's tz, format as the datetime-local input expects.
      setScheduleAt(isoToZonedInput(p.scheduledAt, tz));
    }
    setPrefilled(true);
  }, [isEditing, prefilled, editPostQuery.data, mediaQuery.data, tz]);

  // ── Reuse mode: navigated here from a published post ("Reutilizar post") ──
  // location.state.reuse = { mediaUrls, caption, accountIds }. We resolve the
  // media URLs back to library ids, copy the caption, and pre-select the chosen
  // (previously-unused) networks. Waits for media + accounts to be loaded.
  const [reuseApplied, setReuseApplied] = useState(false);
  useEffect(() => {
    if (reuseApplied || isEditing) return;
    const s = location.state as
      | { reuse?: { mediaUrls: string[]; caption: string; accountIds: string[] } }
      | null;
    const r = s?.reuse;
    if (!r) return;
    const mediaItems = mediaQuery.data?.items;
    const accs = accountsQuery.data?.accounts;
    if (!mediaItems || !accs) return; // wait for both queries

    const idByUrl = new Map(mediaItems.map((m) => [m.url, m.id]));
    const ids = r.mediaUrls.map((u) => idByUrl.get(u)).filter((x): x is string => Boolean(x));
    setSelectedMediaIds(ids);
    setCopy(r.caption ?? '');

    const connected = accs.filter((a) => a.status === 'connected');
    const newTargets: Target[] = [];
    for (const accId of r.accountIds) {
      const acc = connected.find((a) => a.id === accId);
      if (!acc) continue;
      const kinds = SUPPORTED_KINDS[acc.platform];
      newTargets.push({ accountId: acc.id, kind: kinds[0]!, caption: null });
    }
    setTargets(newTargets);

    setReuseApplied(true);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [reuseApplied, isEditing, location, mediaQuery.data, accountsQuery.data, navigate]);

  const setPremiumMutation = useMutation({
    mutationFn: ({ accountId, isPremium }: { accountId: string; isPremium: boolean }) =>
      api<{ account: ConnectedAccount }>(`/api/social/accounts/${accountId}/premium`, {
        method: 'PATCH',
        body: JSON.stringify({ isPremium }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social', 'accounts'] });
    },
  });

  const mediaItems = mediaQuery.data?.items ?? [];
  const accounts   = (accountsQuery.data?.accounts ?? []).filter((a) => a.status === 'connected');
  const platformOfAccount = (id: string) => accounts.find((a) => a.id === id)?.platform;
  /** Instagram feed posts and reels take collaborators; stories don't. */
  const takesCollaborators = (t: Target) => platformOfAccount(t.accountId) === 'instagram' && t.kind !== 'story';

  const selectedMedia = useMemo(
    () => selectedMediaIds.map((id) => mediaItems.find((m) => m.id === id)).filter((m): m is MediaItem => Boolean(m)),
    [selectedMediaIds, mediaItems],
  );

  // The cover belongs to one video: drop it when that video is deselected.
  const selectedVideo = selectedMedia.find((m) => m.kind === 'video') ?? null;
  useEffect(() => {
    if (cover && cover.videoId !== selectedVideo?.id) setCover(null);
  }, [cover, selectedVideo?.id]);
  const showCoverPicker = selectedVideo !== null && targets.some((t) => t.kind !== 'story');
  const showCollaborators = targets.some(takesCollaborators);

  // keep preview index in range
  useEffect(() => {
    if (previewTargetIdx >= targets.length) setPreviewTargetIdx(0);
  }, [targets.length, previewTargetIdx]);

  // TikTok: auto-detect Foto vs Vídeo from the attached media so the user never
  // has to pick. Any video → 'video'; otherwise (images) → 'photo'. Kept in sync
  // whenever the media changes. The `changed` guard avoids a render loop.
  useEffect(() => {
    const desired: PostKind = selectedMedia.some((m) => m.kind === 'video') ? 'video' : 'photo';
    setTargets((prev) => {
      let changed = false;
      const next = prev.map((t) => {
        const acc = accounts.find((a) => a.id === t.accountId);
        const isTikTok = acc?.platform === 'tiktok' || acc?.platform === 'tiktok_business';
        if (isTikTok && t.kind !== desired) {
          changed = true;
          return { ...t, kind: desired };
        }
        return t;
      });
      return changed ? next : prev;
    });
  }, [selectedMedia, accounts, targets]);

  // any non-story target → copy is shown
  const showCopy = targets.length === 0 || targets.some((t) => !KIND_HAS_NO_COPY.has(t.kind));

  function toggleMedia(id: string) {
    setSelectedMediaIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleAccount(account: ConnectedAccount) {
    setTargets((prev) => {
      const exists = prev.find((t) => t.accountId === account.id);
      if (exists) return prev.filter((t) => t.accountId !== account.id);
      const kinds = SUPPORTED_KINDS[account.platform];
      return [...prev, { accountId: account.id, kind: kinds[0]!, caption: null }];
    });
  }

  function setTargetKind(accountId: string, kind: PostKind) {
    setTargets((prev) => prev.map((t) => (t.accountId === accountId ? { ...t, kind } : t)));
  }

  /**
   * Set or clear the per-network caption override for a target.
   *  - Passing a non-empty string enables the override.
   *  - Passing `null` (or empty) reverts to the global `copy`.
   */
  function setTargetCaption(accountId: string, caption: string | null) {
    setTargets((prev) =>
      prev.map((t) => (t.accountId === accountId ? { ...t, caption } : t)),
    );
  }

  /**
   * Validate the form and build the shared submission bits (targets payload +
   * resolved schedule time). Returns null and sets an error message on failure.
   * Used by both "publish" and "request approval".
   */
  function prepareSubmission(): {
    targetsPayload: Array<{ accountId: string; kind: PostKind; caption: string | null; collaborators?: string[] }>;
    scheduledAtIso: string | null;
    needsCopy: boolean;
  } | null {
    if (targets.length === 0) {
      setError('Selecciona al menos una cuenta destino.');
      return null;
    }
    // Media is optional only when every target is a plain-text post on a
    // text-first network (X, LinkedIn, Threads, Bluesky, Facebook). Any other
    // target (Instagram, reels, stories, TikTok, YouTube, Pinterest…) needs a file.
    if (selectedMediaIds.length === 0) {
      const accountById = new Map(accounts.map((a) => [a.id, a]));
      const blocking = targets.find((t) => {
        const acc = accountById.get(t.accountId);
        return !acc || !targetAllowsTextOnly(acc.platform, t.kind);
      });
      if (blocking) {
        const acc = accountById.get(blocking.accountId);
        const name = acc ? PLATFORM_META[acc.platform].label : 'Esta red';
        setError(`${name} requiere al menos un archivo (imagen o vídeo).`);
        return null;
      }
    }
    const needsCopy = targets.some((t) => !KIND_HAS_NO_COPY.has(t.kind));
    if (needsCopy) {
      // Each target that needs a copy must have EITHER its own caption override
      // OR the global copy must be non-empty.
      const globalEmpty = copy.trim().length === 0;
      const missing = targets.find(
        (t) =>
          !KIND_HAS_NO_COPY.has(t.kind) &&
          globalEmpty &&
          (t.caption === null || t.caption.trim().length === 0),
      );
      if (missing) {
        setError('Escribe un copy para tu publicación (o personalízalo por red).');
        return null;
      }
    }

    let scheduledAtIso: string | null = null;
    if (publishMode === 'scheduled') {
      scheduledAtIso = zonedInputToIso(scheduleAt, tz);
    } else if (publishMode === 'queue') {
      if (!nextSlotQuery.data?.scheduledAt) {
        setError('No se pudo calcular el próximo hueco de la cola. Inténtalo en unos segundos.');
        return null;
      }
      scheduledAtIso = nextSlotQuery.data.scheduledAt;
    }

    const targetsPayload = targets.map((t) => ({
      accountId: t.accountId,
      kind: t.kind,
      caption: t.caption && t.caption.trim().length > 0 ? t.caption.trim() : null,
      ...(takesCollaborators(t) && collaborators.length > 0 ? { collaborators } : {}),
    }));

    return { targetsPayload, scheduledAtIso, needsCopy };
  }

  /** Create the post held for client approval and open the share-link modal. */
  async function handleRequestApproval() {
    setError(null);
    setOkMsg(null);
    const prepared = prepareSubmission();
    if (!prepared) return;
    const { targetsPayload, scheduledAtIso, needsCopy } = prepared;

    setRequestingApproval(true);
    try {
      const res = await api<{ approvalToken: string | null; reviewersNotified?: number }>('/api/posts', {
        method: 'POST',
        body: JSON.stringify({
          mediaIds: selectedMediaIds,
          targets: targetsPayload,
          caption: needsCopy ? copy.trim() : null,
          scheduledAt: scheduledAtIso,
          cover: showCoverPicker && cover ? { url: cover.url, ms: cover.ms } : null,
          requestApproval: true,
        }),
      });
      if (res.approvalToken) {
        setApprovalLink(`${window.location.origin}/approve/${res.approvalToken}`);
        setApprovalReviewers(res.reviewersNotified ?? 0);
      }
      // reset partial state (same as a successful publish)
      setCopy('');
      setSelectedMediaIds([]);
      setCover(null);
      setCollaborators([]);
      setTargets([]);
      setPublishMode('now');
      setScheduleAt(defaultScheduleValue(tz));
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_MEDIA') setError('Alguno de los archivos seleccionados ya no existe.');
      else if (err instanceof ApiError && err.code === 'INVALID_ACCOUNT') setError('Alguna de las cuentas seleccionadas ya no existe.');
      else if (err instanceof ApiError && err.code === 'SUBSCRIPTION_REQUIRED') setError('Necesitas una suscripción activa para pedir aprobación.');
      else setError('No se pudo crear la solicitud de aprobación. Inténtalo de nuevo.');
    } finally {
      setRequestingApproval(false);
    }
  }

  async function handlePublish() {
    setError(null);
    setOkMsg(null);

    const prepared = prepareSubmission();
    if (!prepared) return;
    const { targetsPayload, scheduledAtIso, needsCopy } = prepared;

    // Build the payload. Create takes mediaIds (validated against the user's
    // media table); update takes `media: { url, type }[]` instead because the
    // edit form starts off with URL-only media (the original IDs aren't on
    // the post itself). We resolve IDs → media items for the edit case.
    setPublishing(true);
    try {
      if (isEditing && editId) {
        const items = mediaQuery.data?.items ?? [];
        const byId = new Map(items.map((m) => [m.id, m]));
        const mediaPayload = selectedMediaIds
          .map((id) => byId.get(id))
          .filter((m): m is MediaItem => Boolean(m))
          .map((m) => ({ url: m.url, type: m.kind }));
        // Media requirement is already enforced by prepareSubmission() above
        // (text-only posts on X/LinkedIn/etc. are allowed with no files).
        await api(`/api/posts/${editId}`, {
          method: 'PUT',
          body: JSON.stringify({
            media: mediaPayload,
            targets: targetsPayload,
            caption: needsCopy ? copy.trim() : null,
            scheduledAt: scheduledAtIso,
            cover: showCoverPicker && cover ? { url: cover.url, ms: cover.ms } : null,
          }),
        });
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        queryClient.invalidateQueries({ queryKey: ['post', editId] });
        navigate('/dashboard/posts');
        return;
      }

      const payload = {
        mediaIds: selectedMediaIds,
        targets: targetsPayload,
        caption: needsCopy ? copy.trim() : null,
        scheduledAt: scheduledAtIso,
        cover: showCoverPicker && cover ? { url: cover.url, ms: cover.ms } : null,
      };
      await api('/api/posts', { method: 'POST', body: JSON.stringify(payload) });
      // Snapshot account info for each target so we can show a rich success banner
      // even after we clear the targets state below.
      const okTargets: OkMsgTarget[] = targets.map((t) => {
        const a = accounts.find((acc) => acc.id === t.accountId);
        return {
          platform: (a?.platform ?? 'twitter') as Platform,
          username: a?.username ?? null,
          displayName: a?.displayName ?? null,
          profilePhotoUrl: a?.profilePhotoUrl ?? null,
        };
      });
      setOkMsg({ mode: publishMode, scheduledAtIso, targets: okTargets });
      // reset partial state
      setCopy('');
      setSelectedMediaIds([]);
      setCover(null);
      setCollaborators([]);
      setTargets([]);
      setPublishMode('now');
      setScheduleAt(defaultScheduleValue(tz));
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['queue', 'next'] });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_MEDIA') setError('Alguno de los archivos seleccionados ya no existe.');
      else if (err instanceof ApiError && err.code === 'INVALID_ACCOUNT') setError('Alguna de las cuentas seleccionadas ya no existe.');
      else if (err instanceof ApiError && err.code === 'NOT_EDITABLE') setError('Esta publicación ya no puede editarse.');
      else if (err instanceof ApiError && err.code === 'NOT_FOUND') setError('No se encontró la publicación.');
      else setError(isEditing ? 'No se pudo actualizar la publicación.' : 'No se pudo crear la publicación. Inténtalo de nuevo.');
    } finally {
      setPublishing(false);
    }
  }

  const currentTarget = targets[previewTargetIdx];
  const currentAccount = currentTarget ? accounts.find((a) => a.id === currentTarget.accountId) ?? null : null;

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PenSquare size={22} className="text-brand-600" />
            {isEditing ? 'Editar publicación' : 'Crear publicación'}
          </h1>
          <p className="mt-2 text-slate-600">
            {isEditing
              ? 'Cambia el texto, la media, las redes o la fecha — los cambios se guardan al pulsar el botón.'
              : 'Sube tu media, elige tus redes y visualiza cómo se verá antes de publicar.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-6 card border-red-200 bg-red-50 flex items-center justify-between gap-4">
          <p className="text-sm text-red-700">{error}</p>
          <Link
            to={`/dashboard/help?subject=${encodeURIComponent('Error al crear publicación')}&body=${encodeURIComponent(`Hola, he tenido el siguiente error al intentar crear una publicación:\n\n"${error}"\n\n`)}`}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-red-700 hover:text-red-900 underline underline-offset-2 whitespace-nowrap"
          >
            <MessageSquare size={13} />
            Abrir ticket de soporte
          </Link>
        </div>
      )}
      {okMsg && (
        <SuccessBanner okMsg={okMsg} tz={tz} onClose={() => setOkMsg(null)} />
      )}

      {/* 3-column layout: collapses to stacked on smaller screens */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] xl:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)] gap-4">
        <MediaPanel
          media={mediaItems}
          loading={mediaQuery.isLoading}
          selectedIds={selectedMediaIds}
          onToggle={toggleMedia}
          onRefresh={() => queryClient.invalidateQueries({ queryKey: ['media'] })}
        />

        <ComposerPanel
          accounts={accounts}
          accountsLoading={accountsQuery.isLoading}
          targets={targets}
          onToggleAccount={toggleAccount}
          onSetTargetKind={setTargetKind}
          onSetTargetCaption={setTargetCaption}
          copy={copy}
          setCopy={setCopy}
          showCopy={showCopy}
          publishMode={publishMode}
          setPublishMode={setPublishMode}
          scheduleAt={scheduleAt}
          setScheduleAt={setScheduleAt}
          nextSlotIso={nextSlotQuery.data?.scheduledAt ?? null}
          nextSlotLoading={nextSlotQuery.isLoading}
          onOpenQueueModal={() => setQueueModalOpen(true)}
          onOpenAiModal={() => setAiModalOpen(true)}
          onOpenCaptionModal={() => setCaptionModalOpen(true)}
          onOpenIdeasModal={() => setIdeasModalOpen(true)}
          onPublish={handlePublish}
          onRequestApproval={handleRequestApproval}
          requestingApproval={requestingApproval}
          publishing={publishing}
          selectedMediaCount={selectedMedia.length}
          tz={tz}
          onSetAccountPremium={(accountId, isPremium) => setPremiumMutation.mutate({ accountId, isPremium })}
          settingPremium={setPremiumMutation.isPending}
          isEditing={isEditing}
          extras={
            showCoverPicker || showCollaborators ? (
              <div className="space-y-3">
                {showCoverPicker && selectedVideo && (
                  <VideoCoverPicker
                    key={selectedVideo.id}
                    video={selectedVideo}
                    images={mediaItems.filter((m) => m.kind === 'image' && m.status === 'ready')}
                    value={cover}
                    onChange={setCover}
                  />
                )}
                {showCollaborators && <CollaboratorsInput value={collaborators} onChange={setCollaborators} />}
              </div>
            ) : null
          }
        />

        <PreviewPanel
          targets={targets}
          accounts={accounts}
          activeIdx={previewTargetIdx}
          setActiveIdx={setPreviewTargetIdx}
          media={selectedMedia}
          copy={copy}
          currentAccount={currentAccount}
          currentTarget={currentTarget}
        />
      </div>

      {queueModalOpen && (
        <QueueSlotsModal onClose={() => setQueueModalOpen(false)} />
      )}

      {approvalLink && (
        <ApprovalLinkModal
          link={approvalLink}
          reviewersNotified={approvalReviewers}
          onClose={() => setApprovalLink(null)}
        />
      )}

      <AiPredictionModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        caption={copy}
        media={selectedMedia.map((m) => ({ id: m.id, url: m.url, kind: m.kind }))}
      />

      <AiCaptionModal
        open={captionModalOpen}
        onClose={() => setCaptionModalOpen(false)}
        accounts={targets
          .map((t) => accounts.find((a) => a.id === t.accountId))
          .filter((a): a is ConnectedAccount => Boolean(a))
          .map((a) => ({
            id: a.id,
            platform: a.platform,
            username: a.username,
            displayName: a.displayName,
          }))}
        media={selectedMedia.map((m) => ({ id: m.id, url: m.url, kind: m.kind }))}
        onInsert={(text) => setCopy((prev) => (prev.trim() ? `${prev}\n\n${text}` : text))}
      />

      <AiIdeasModal
        open={ideasModalOpen}
        onClose={() => setIdeasModalOpen(false)}
        accounts={targets
          .map((t) => accounts.find((a) => a.id === t.accountId))
          .filter((a): a is ConnectedAccount => Boolean(a))
          .map((a) => ({
            id: a.id,
            platform: a.platform,
            username: a.username,
            displayName: a.displayName,
          }))}
        onUseIdea={(text) => setCopy((prev) => (prev.trim() ? `${prev}\n\n${text}` : text))}
      />
    </>
  );
}

function QueueSlotsModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Cola de publicación</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Edita los horarios en los que se publicarán las publicaciones añadidas a la cola.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-6">
          <QueueSlotsEditor onSaved={onClose} />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   LEFT — Media library
   ─────────────────────────────────────────────────────────── */
/* Thumbnail <img> that survives the brief window after upload where the
   object-storage URL isn't readable yet (eventual consistency): it retries a
   few times with backoff + cache-busting, then falls back to a clean
   placeholder instead of the browser's broken-image glyph. */
function ThumbImage({ src, alt }: { src: string; alt: string }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  // Re-arm loading whenever the source changes (e.g. list refetch).
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);

  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-300">
        <ImageIcon size={18} />
      </div>
    );
  }

  const url = attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}r=${attempt}`;
  return (
    <img
      key={attempt}
      src={url}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => {
        if (attempt < 3) {
          setTimeout(() => setAttempt((a) => a + 1), 600 * (attempt + 1));
        } else {
          setFailed(true);
        }
      }}
    />
  );
}

function MediaPanel(props: {
  media: MediaItem[];
  loading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onRefresh: () => void;
}) {
  const { media, loading, selectedIds, onToggle, onRefresh } = props;
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState<{ name: string; pct: number }[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(9);
  const PAGE = 9;

  // reset pagination on new search
  useEffect(() => { setVisibleCount(PAGE); }, [query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return media;
    return media.filter((m) =>
      (m.originalName ?? '').toLowerCase().includes(q),
    );
  }, [media, query]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visible.length;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/media/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['media'] }),
  });

  async function uploadFiles(files: FileList | File[]) {
    setUploadError(null);
    const list = Array.from(files);
    if (list.length === 0) return;

    const pending = list.map((f) => ({ name: f.name, pct: 0 }));
    setUploading(pending);

    for (let i = 0; i < list.length; i++) {
      const file = list[i]!;
      try {
        const presigned = await api<{
          id: string;
          uploadUrl: string;
          headers: Record<string, string>;
        }>('/api/media/upload-url', {
          method: 'POST',
          body: JSON.stringify({
            mimeType: file.type,
            sizeBytes: file.size,
            originalName: file.name,
          }),
        });

        await putWithProgress(presigned.uploadUrl, file, presigned.headers, (pct) =>
          setUploading((u) => u.map((row, idx) => (idx === i ? { ...row, pct } : row))),
        );

        await api('/api/media/confirm', {
          method: 'POST',
          body: JSON.stringify({ id: presigned.id }),
        });

        setUploading((u) => u.map((row, idx) => (idx === i ? { ...row, pct: 100 } : row)));
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'Error al subir el archivo';
        setUploadError(`${file.name}: ${msg}`);
      }
    }

    onRefresh();
    setTimeout(() => setUploading([]), 800);
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) uploadFiles(e.target.files);
    e.target.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files);
  }

  return (
    <aside
      onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragOver={(e)  => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => {
        // only clear when leaving the panel itself, not crossing inner children
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragOver(false);
      }}
      onDrop={onDrop}
      className={cn(
        'card p-0 flex flex-col h-fit lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] relative transition',
        dragOver && 'ring-2 ring-brand-400 ring-offset-1',
      )}
    >
      <div className="px-4 py-3 border-b border-slate-100 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ImageIcon size={16} className="text-slate-500" />
            <h2 className="font-semibold text-slate-900 text-sm">Media</h2>
            {media.length > 0 && (
              <span className="text-xs text-slate-400">{media.length}</span>
            )}
          </div>
          <button
            onClick={() => inputRef.current?.click()}
            className="text-xs px-2.5 py-1 rounded-md bg-brand-600 text-white font-medium hover:bg-brand-700 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Subir
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={onInputChange}
          />
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre…"
            className="w-full pl-7 pr-7 py-1.5 text-xs rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Limpiar búsqueda"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {uploadError && (
        <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-b border-red-100">{uploadError}</div>
      )}

      {uploading.length > 0 && (
        <div className="px-4 py-3 border-b border-slate-100 space-y-2">
          {uploading.map((u, i) => (
            <div key={i} className="text-xs">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="truncate min-w-0 text-slate-700">{u.name}</span>
                <span className="shrink-0 text-slate-500">{u.pct}%</span>
              </div>
              <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-brand-500 transition-all" style={{ width: `${u.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 min-h-[180px]">
        {loading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2 py-6 justify-center">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : media.length === 0 ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full h-full min-h-[160px] flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-200 text-slate-400 hover:border-brand-300 hover:text-brand-600 transition"
          >
            <Upload size={20} />
            <span className="text-xs font-medium">Arrastra o pulsa para subir</span>
          </button>
        ) : visible.length === 0 ? (
          <div className="text-slate-400 text-xs text-center py-8">
            Ningún archivo coincide con "{query}".
          </div>
        ) : (
          <ul className="grid grid-cols-3 gap-1">
            {visible.map((m) => {
              const idx = selectedIds.indexOf(m.id);
              const selected = idx !== -1;
              return (
                <li key={m.id} className="relative group">
                  <button
                    onClick={() => props.onToggle(m.id)}
                    className={cn(
                      'block w-full aspect-square rounded-lg overflow-hidden border-2 transition relative bg-slate-100',
                      selected ? 'border-brand-500 ring-2 ring-brand-200' : 'border-transparent hover:border-slate-300',
                    )}
                  >
                    {m.kind === 'image' ? (
                      <ThumbImage src={m.thumbnailUrl ?? m.url} alt={m.originalName ?? ''} />
                    ) : (
                      <>
                        <video src={m.url} className="w-full h-full object-cover" muted preload="metadata" />
                        <span className="absolute bottom-0.5 right-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-black/60 text-white">
                          <Play size={7} fill="white" />
                        </span>
                      </>
                    )}
                    {selected && (
                      <span className="absolute top-1 left-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-white text-[12px] font-bold ring-2 ring-white shadow-sm">
                        {idx + 1}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('¿Eliminar este archivo?')) deleteMutation.mutate(m.id);
                    }}
                    className="absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/95 text-slate-700 opacity-0 group-hover:opacity-100 transition shadow-sm hover:bg-red-50 hover:text-red-600"
                    aria-label="Eliminar"
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {hasMore && (
          <button
            onClick={() => setVisibleCount((c) => c + PAGE)}
            className="mt-3 w-full text-xs py-1.5 rounded-md border border-slate-200 text-slate-600 font-medium hover:border-brand-300 hover:text-brand-600 transition"
          >
            Cargar más ({filtered.length - visible.length})
          </button>
        )}
      </div>

      {/* drag-over overlay covers the whole panel */}
      {dragOver && (
        <div className="absolute inset-0 z-10 rounded-2xl bg-brand-50/90 border-2 border-dashed border-brand-400 flex flex-col items-center justify-center gap-2 text-brand-700 pointer-events-none">
          <Upload size={28} />
          <span className="text-sm font-semibold">Suelta para subir</span>
        </div>
      )}
    </aside>
  );
}

/* X (Twitter) verified / premium blue checkmark — shown only when the
   connected account is marked as premium. */
/* Compact AI quick-action chip used in the composer. Keeps the same gradient
   identity as the old big card but in a single row to save vertical space. */
function AiQuickAction({
  icon: Icon,
  label,
  gradient,
  hoverBg,
  hoverBorder,
  textColor,
  borderColor,
  disabled,
  tooltip,
  onClick,
}: {
  icon: typeof Lightbulb;
  label: string;
  gradient: string;
  hoverBg: string;
  hoverBorder: string;
  textColor: string;
  borderColor: string;
  disabled: boolean;
  tooltip: string;
  onClick: () => void;
}) {
  return (
    <span className="relative group/aichip">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={tooltip}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white',
          disabled ? 'border-slate-200 text-slate-500' : cn(borderColor, textColor, hoverBg, hoverBorder),
        )}
      >
        <span className={cn(
          'h-5 w-5 rounded-md bg-gradient-to-br flex items-center justify-center text-white shadow-sm shrink-0',
          gradient,
        )}>
          <Icon size={11} />
        </span>
        {label}
        <span className={cn(
          'text-[9px] font-bold uppercase tracking-wide px-1 py-px rounded bg-gradient-to-r text-white',
          gradient,
        )}>
          IA
        </span>
      </button>

      {/* Hover tooltip */}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-40 mt-2 w-60 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-normal leading-snug text-white shadow-lg opacity-0 invisible transition-opacity duration-150 group-hover/aichip:opacity-100 group-hover/aichip:visible"
      >
        <span
          aria-hidden
          className="absolute left-1/2 top-0 -mt-1 h-2 w-2 -translate-x-1/2 rotate-45 bg-slate-900"
        />
        {tooltip}
      </span>
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
   CENTER — Composer
   ─────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────
   Account picker
   ─────────────────────────────────────────────────────────── */

/** Up to this many accounts the plain grid is quicker to scan than any search
 *  is to use. Past it — agencies run dozens — the grid became a wall of cards
 *  and finding one account meant reading all of them. */
const PICKER_SEARCH_THRESHOLD = 6;

/** Lowercase and strip accents, so "cotillon" finds "Cotillón". */
function normalizeForSearch(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function AccountPicker(props: {
  accounts: ConnectedAccount[];
  targets: Target[];
  onToggleAccount: (account: ConnectedAccount) => void;
  onSetTargetKind: (accountId: string, kind: PostKind) => void;
}) {
  const { accounts, targets, onToggleAccount, onSetTargetKind } = props;
  const [query, setQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState<Platform | null>(null);

  const targetByAccountId = useMemo(() => {
    const m = new Map<string, Target>();
    for (const t of targets) m.set(t.accountId, t);
    return m;
  }, [targets]);

  /** The full card: avatar, name and — once selected — the format picker. */
  function renderCard(a: ConnectedAccount) {
    const meta = PLATFORM_META[a.platform];
    const target = targetByAccountId.get(a.id);
    const selected = Boolean(target);
    const kinds = SUPPORTED_KINDS[a.platform];
    // TikTok's format (Foto/Vídeo) is auto-detected from the media, so
    // we don't show a manual picker for it — just the detected format.
    const isTikTok = a.platform === 'tiktok' || a.platform === 'tiktok_business';

    return (
      <li
        key={a.id}
        className={cn(
          'rounded-xl border p-2.5 transition',
          selected ? 'border-brand-300 bg-brand-50/50' : 'border-slate-200 hover:border-slate-300',
        )}
      >
        <button
          onClick={() => onToggleAccount(a)}
          className="w-full flex items-center gap-2.5 text-left"
        >
          <AccountAvatar account={a} size={32} selected={selected} />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1 text-sm font-medium text-slate-900">
              <span className="truncate">
                {a.displayName ?? a.username ?? meta.label}
              </span>
              {a.platform === 'twitter' && a.isPremium && <XVerifiedBadge size={14} />}
            </span>
            <span className="block text-[11px] text-slate-500 truncate">
              {meta.label}
            </span>
          </span>
        </button>
        {selected && isTikTok && (
          <div className="mt-2">
            <span
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200"
              title="TikTok detecta el formato automáticamente según la media que adjuntes"
            >
              {KIND_LABEL[target!.kind]} · automático
            </span>
          </div>
        )}
        {selected && !isTikTok && kinds.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {kinds.map((k) => (
              <button
                key={k}
                onClick={() => onSetTargetKind(a.id, k)}
                className={cn(
                  'text-[11px] px-2 py-0.5 rounded-full border transition',
                  target!.kind === k
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                )}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
        )}
      </li>
    );
  }

  if (accounts.length <= PICKER_SEARCH_THRESHOLD) {
    return <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">{accounts.map(renderCard)}</ul>;
  }

  // Selected accounts leave the list and sit on top with their format
  // pickers: what you're publishing to stays in view no matter how far down
  // the list, or how narrow the search, you happen to be.
  const selected = accounts.filter((a) => targetByAccountId.has(a.id));
  const available = accounts.filter((a) => !targetByAccountId.has(a.id));

  const q = normalizeForSearch(query.trim());
  const matching = q
    ? available.filter((a) =>
        normalizeForSearch(
          [a.displayName, a.username, PLATFORM_META[a.platform].label].filter(Boolean).join(' '),
        ).includes(q),
      )
    : available;

  // Every network the user has, so the chips don't jump around as the search
  // narrows — only their counts change, and a network with no match greys out.
  const platforms = [...new Set(accounts.map((a) => a.platform))];
  const countByPlatform = new Map<Platform, number>();
  for (const a of matching) countByPlatform.set(a.platform, (countByPlatform.get(a.platform) ?? 0) + 1);

  const visible = platformFilter ? matching.filter((a) => a.platform === platformFilter) : matching;

  return (
    <div className="space-y-3">
      {selected.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-500">
              Seleccionadas ({selected.length})
            </span>
            {selected.length > 1 && (
              <button
                type="button"
                onClick={() => selected.forEach(onToggleAccount)}
                className="text-xs text-slate-500 hover:text-red-600 transition"
              >
                Quitar todas
              </button>
            )}
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">{selected.map(renderCard)}</ul>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-2.5 space-y-2.5">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
            placeholder={`Buscar entre tus ${accounts.length} cuentas…`}
            className="input pl-9 pr-9 bg-white"
            aria-label="Buscar cuenta"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600"
              aria-label="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {platforms.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setPlatformFilter(null)}
              className={cn(
                'text-xs px-2.5 py-1 rounded-full border transition tabular-nums',
                platformFilter === null
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
              )}
            >
              Todas · {matching.length}
            </button>
            {platforms.map((p) => {
              const meta = PLATFORM_META[p];
              const Icon = meta.icon;
              const count = countByPlatform.get(p) ?? 0;
              const isOn = platformFilter === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatformFilter(isOn ? null : p)}
                  disabled={count === 0 && !isOn}
                  className={cn(
                    'inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition tabular-nums',
                    isOn
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                    count === 0 && !isOn && 'opacity-40 cursor-not-allowed',
                  )}
                >
                  <Icon size={12} color={isOn ? 'white' : meta.color} />
                  {meta.label} · {count}
                </button>
              );
            })}
          </div>
        )}

        {visible.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">
            {available.length === 0 ? (
              'Ya has elegido todas tus cuentas.'
            ) : (
              <>
                Ninguna cuenta coincide
                {query ? <> con «{query}»</> : null}
                {platformFilter ? <> en {PLATFORM_META[platformFilter].label}</> : null}.{' '}
                <button
                  type="button"
                  onClick={() => { setQuery(''); setPlatformFilter(null); }}
                  className="text-brand-600 font-medium hover:underline"
                >
                  Ver todas
                </button>
              </>
            )}
          </div>
        ) : (
          // Bounded and scrolling inside itself: thirty-odd accounts used to
          // push the caption, the media and the publish button off the page.
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-72 overflow-y-auto pr-1 -mr-1">
            {visible.map((a) => {
              const meta = PLATFORM_META[a.platform];
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => onToggleAccount(a)}
                    className="group w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-white hover:shadow-sm transition"
                  >
                    <AccountAvatar account={a} size={28} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1 text-sm text-slate-800">
                        <span className="truncate">{a.displayName ?? a.username ?? meta.label}</span>
                        {a.platform === 'twitter' && a.isPremium && <XVerifiedBadge size={13} />}
                      </span>
                      <span className="block text-[11px] text-slate-400 truncate">{meta.label}</span>
                    </span>
                    <Plus
                      size={15}
                      className="shrink-0 text-slate-300 group-hover:text-brand-600 transition"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ComposerPanel(props: {
  accounts: ConnectedAccount[];
  accountsLoading: boolean;
  targets: Target[];
  onToggleAccount: (account: ConnectedAccount) => void;
  onSetTargetKind: (accountId: string, kind: PostKind) => void;
  onSetTargetCaption: (accountId: string, caption: string | null) => void;
  copy: string;
  setCopy: (v: string) => void;
  showCopy: boolean;
  publishMode: PublishMode;
  setPublishMode: (v: PublishMode) => void;
  scheduleAt: string;
  setScheduleAt: (v: string) => void;
  nextSlotIso: string | null;
  nextSlotLoading: boolean;
  onOpenQueueModal: () => void;
  onOpenAiModal: () => void;
  onOpenCaptionModal: () => void;
  onOpenIdeasModal: () => void;
  onPublish: () => void;
  onRequestApproval: () => void;
  requestingApproval: boolean;
  publishing: boolean;
  selectedMediaCount: number;
  tz: string;
  onSetAccountPremium: (accountId: string, isPremium: boolean) => void;
  settingPremium: boolean;
  isEditing: boolean;
  /** Format-specific options (video cover, Instagram collaborators). */
  extras?: ReactNode;
}) {
  const {
    accounts, accountsLoading, targets, onToggleAccount, onSetTargetKind, onSetTargetCaption,
    copy, setCopy, showCopy, publishMode, setPublishMode, scheduleAt, setScheduleAt,
    nextSlotIso, nextSlotLoading, onOpenQueueModal, onOpenAiModal, onOpenCaptionModal, onOpenIdeasModal,
    onPublish, onRequestApproval, requestingApproval, publishing, selectedMediaCount, tz,
    onSetAccountPremium, settingPremium, isEditing, extras,
  } = props;

  /**
   * "General" + 1 pestaña por destino. La pestaña activa decide qué textarea
   * se muestra: la global (escribe en `copy`) o la específica de un target
   * (escribe en `target.caption`). Solo aparecen las pestañas con 2+ destinos:
   * con un solo destino la personalización es indistinguible del general.
   */
  const captionTextTargets = targets.filter((t) => !KIND_HAS_NO_COPY.has(t.kind));
  const showCaptionTabs = captionTextTargets.length >= 2;
  // 'general' | accountId
  const [captionTab, setCaptionTab] = useState<string>('general');
  // Si el destino activo se deselecciona, volvemos a "general".
  useEffect(() => {
    if (captionTab !== 'general' && !targets.some((t) => t.accountId === captionTab)) {
      setCaptionTab('general');
    }
  }, [targets, captionTab]);

  const activeTarget = captionTab === 'general' ? null : targets.find((t) => t.accountId === captionTab) ?? null;
  const editingOverride = activeTarget !== null;
  // Cuando se edita un target sin override propio, mostramos el `copy` como
  // valor inicial (read-only hasta que pulse "Personalizar"). Si tiene override,
  // editamos el override directamente.
  const editingValue: string = editingOverride
    ? activeTarget!.caption !== null
      ? activeTarget!.caption!
      : copy
    : copy;
  const editingHasOverride = editingOverride && activeTarget!.caption !== null;
  const editingPlaceholder = editingOverride
    ? `Versión específica para esta red…`
    : 'Escribe el texto de tu publicación…';
  const canPredict = copy.trim().length > 0 || selectedMediaCount > 0;
  const canGenerateCaption = targets.length > 0 && selectedMediaCount > 0;
  const canGenerateIdeas = targets.length > 0;

  // For each target compute the actual text it will send (override or global)
  // and whether that text exceeds the account's effective character limit
  // (which depends on platform + premium status).
  const targetLimits = useMemo(() => {
    return targets
      .map((t) => {
        const acc = accounts.find((a) => a.id === t.accountId);
        if (!acc) return null;
        const limit = effectiveCharLimit(acc.platform, acc.isPremium);
        const text = t.caption !== null ? t.caption : copy;
        return {
          target: t,
          account: acc,
          limit,
          text,
          length: text.length,
          over: limit != null && text.length > limit,
          overBy: limit != null ? Math.max(0, text.length - limit) : 0,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [targets, accounts, copy]);

  // Limits relevant to the textarea currently being edited:
  //  - General tab: any target whose text comes from `copy` (= no override)
  //  - Specific tab: just that target.
  const relevantLimits = editingOverride
    ? targetLimits.filter((tl) => tl.target.accountId === activeTarget!.accountId)
    : targetLimits.filter((tl) => tl.target.caption === null);

  // Strictest limit (smallest non-null) among the relevant targets.
  const editingLimit = relevantLimits.reduce<number | null>((min, tl) => {
    if (tl.limit == null) return min;
    return min == null ? tl.limit : Math.min(min, tl.limit);
  }, null);
  const editingOverLimit = editingLimit != null && editingValue.length > editingLimit;
  const overflowingTargets = relevantLimits.filter((tl) => tl.over);

  return (
    <section className="card flex flex-col gap-6">
      {/* Accounts */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-900">Publicar en</h2>
          {targets.length > 0 && (
            <span className="text-xs text-slate-500">{targets.length} cuenta(s)</span>
          )}
        </div>

        {accountsLoading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2 py-4">
            <Loader2 size={14} className="animate-spin" /> Cargando cuentas…
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
            No tienes cuentas conectadas.{' '}
            <a href="/dashboard/accounts" className="text-brand-600 font-medium hover:underline">
              Conecta una red social →
            </a>
          </div>
        ) : (
          <AccountPicker
            accounts={accounts}
            targets={targets}
            onToggleAccount={onToggleAccount}
            onSetTargetKind={onSetTargetKind}
          />
        )}
      </div>

      {/* Copy */}
      {showCopy && (
        <div>
          <label className="label flex items-center justify-between">
            <span>Texto de la publicación</span>
            <span className={cn(
              'text-xs font-normal tabular-nums',
              editingOverLimit ? 'text-red-600 font-semibold' : 'text-slate-400',
            )}>
              {editingValue.length}
              {editingLimit != null && (
                <span className="text-slate-400"> / {editingLimit.toLocaleString('es-ES')}</span>
              )}
            </span>
          </label>

          {/* Tabs: General + 1 por red (solo con 2+ destinos) */}
          {showCaptionTabs && (
            <div className="mt-1 mb-2 flex flex-wrap items-center gap-1.5 border-b border-slate-100 pb-2">
              <button
                type="button"
                onClick={() => setCaptionTab('general')}
                className={cn(
                  'inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border transition',
                  captionTab === 'general'
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300',
                )}
                title="Texto general usado por las redes que no estén personalizadas"
              >
                <Sparkles size={11} />
                General
              </button>
              {captionTextTargets.map((t) => {
                const acc = accounts.find((a) => a.id === t.accountId);
                if (!acc) return null;
                const meta = PLATFORM_META[acc.platform];
                const Icon = meta.icon;
                const active = captionTab === t.accountId;
                const customized = t.caption !== null;
                return (
                  <button
                    key={t.accountId}
                    type="button"
                    onClick={() => setCaptionTab(t.accountId)}
                    className={cn(
                      'inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border transition relative',
                      active
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300',
                    )}
                    title={`${meta.label}${customized ? ' · texto personalizado' : ''}`}
                  >
                    <Icon size={11} color={meta.color} />
                    <span className="truncate max-w-[80px]">
                      {acc.username ?? acc.displayName ?? meta.label}
                    </span>
                    {customized && (
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full bg-amber-500"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* CTA "Personalizar" cuando estamos en una pestaña de red sin override */}
          {editingOverride && !editingHasOverride && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
              <span className="text-[11px] text-slate-500 leading-snug">
                Esta red está usando el texto general. Personalízalo para adaptarlo a su estilo.
              </span>
              <button
                type="button"
                onClick={() => onSetTargetCaption(activeTarget!.accountId, copy)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-700 bg-white border border-brand-200 hover:bg-brand-50 px-2.5 py-1 rounded-md transition whitespace-nowrap"
              >
                Personalizar para esta red
              </button>
            </div>
          )}

          {/* Banner cuando hay override activo */}
          {editingOverride && editingHasOverride && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-md bg-amber-50/60 border border-amber-200 px-3 py-1.5">
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Texto personalizado para esta red
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onSetTargetCaption(activeTarget!.accountId, copy)}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                  title="Vuelve a copiar el texto general aquí"
                >
                  Resetear al general
                </button>
                <button
                  type="button"
                  onClick={() => onSetTargetCaption(activeTarget!.accountId, null)}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                  title="Esta red vuelve a usar el texto general"
                >
                  Usar el general
                </button>
              </div>
            </div>
          )}

          <textarea
            value={editingValue}
            onChange={(e) => {
              const v = e.target.value;
              if (editingOverride) {
                // - Escribir activa el override en el primer tecleo (sin clicks extra).
                // - Borrarlo todo y dejarlo vacío revierte automáticamente al texto general
                //   (caption = null). Tipear otra vez vuelve a activar el override.
                onSetTargetCaption(activeTarget!.accountId, v.length === 0 ? null : v);
              } else {
                setCopy(v);
              }
            }}
            placeholder={editingPlaceholder}
            rows={5}
            className={cn(
              'input resize-y',
              editingOverride && editingHasOverride && 'border-amber-200 focus:border-amber-400 focus:ring-amber-200',
            )}
          />
          <p className="mt-1 text-xs text-slate-400">
            {editingOverride
              ? 'Este texto solo se usará en esta red. El general no se modifica.'
              : 'Cada red tiene su propio límite. La preview lo recortará si hace falta.'}
          </p>

          {/* Overflow preview: show the chars that won't fit, in red */}
          {editingOverLimit && editingLimit != null && (
            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs">
              <p className="text-red-700 font-medium mb-1">
                Este texto excede el límite{' '}
                <span className="tabular-nums">
                  ({editingValue.length.toLocaleString('es-ES')} / {editingLimit.toLocaleString('es-ES')})
                </span>
                . Esto no entrará en la publicación:
              </p>
              <p className="text-red-700 bg-red-100/60 rounded px-2 py-1 whitespace-pre-wrap break-words">
                <span className="text-slate-500">…{editingValue.slice(Math.max(0, editingLimit - 20), editingLimit)}</span>
                <span className="bg-red-200 text-red-900 rounded">{editingValue.slice(editingLimit)}</span>
              </p>
            </div>
          )}

          {/* Per-target warning banners for over-limit accounts */}
          {overflowingTargets.length > 0 && (
            <div className="mt-2 space-y-2">
              {overflowingTargets.map((tl) => {
                const meta = PLATFORM_META[tl.account.platform];
                const limits = PLATFORM_CHAR_LIMITS[tl.account.platform];
                const canUpgrade =
                  tl.account.platform === 'twitter' &&
                  !tl.account.isPremium &&
                  limits.premium != null;
                return (
                  <div
                    key={tl.target.accountId}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2"
                  >
                    <AccountAvatar account={tl.account} size={24} />
                    <div className="flex-1 min-w-0 text-xs">
                      <p className="text-amber-900">
                        <span className="font-semibold">
                          {tl.account.displayName || tl.account.username || meta.label}
                        </span>{' '}
                        en {meta.label} permite hasta{' '}
                        <span className="tabular-nums">{tl.limit!.toLocaleString('es-ES')}</span>{' '}
                        caracteres. Tu texto tiene{' '}
                        <span className="tabular-nums font-semibold">{tl.length.toLocaleString('es-ES')}</span>{' '}
                        ({tl.overBy.toLocaleString('es-ES')} de más).
                        {' '}Si lo publicas así, se cortará a partir de ese carácter.
                      </p>
                      {canUpgrade && (
                        <p className="mt-1 text-amber-800">
                          ¿Tienes X Premium? Con Premium puedes programar hasta{' '}
                          <strong>2.200</strong> caracteres.
                        </p>
                      )}
                    </div>
                    {canUpgrade && (
                      <button
                        type="button"
                        onClick={() => onSetAccountPremium(tl.account.id, true)}
                        disabled={settingPremium}
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-900 bg-white hover:bg-amber-100 border border-amber-300 rounded-md px-2 py-1 transition whitespace-nowrap disabled:opacity-60"
                      >
                        Soy premium
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* AI quick-actions — compact row */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <AiQuickAction
              icon={Lightbulb}
              label="Ideas"
              gradient="from-amber-500 to-pink-500"
              hoverBg="hover:bg-amber-50"
              hoverBorder="hover:border-amber-300"
              textColor="text-amber-700"
              borderColor="border-amber-200"
              disabled={!canGenerateIdeas}
              tooltip={canGenerateIdeas
                ? 'Te damos 10 ideas listas para publicar, adaptadas a tu marca y red social.'
                : 'Selecciona al menos una red social para generar ideas.'}
              onClick={onOpenIdeasModal}
            />
            <AiQuickAction
              icon={Wand2}
              label="Caption"
              gradient="from-accent-500 to-pink-500"
              hoverBg="hover:bg-accent-50"
              hoverBorder="hover:border-accent-300"
              textColor="text-accent-700"
              borderColor="border-accent-200"
              disabled={!canGenerateCaption}
              tooltip={canGenerateCaption
                ? 'Escribiremos el caption a partir de tu media, adaptado a la primera red elegida.'
                : 'Añade media y una red social para poder generar un caption.'}
              onClick={onOpenCaptionModal}
            />
            <AiQuickAction
              icon={Sparkles}
              label="Predicción"
              gradient="from-brand-500 to-purple-600"
              hoverBg="hover:bg-brand-50"
              hoverBorder="hover:border-brand-300"
              textColor="text-brand-700"
              borderColor="border-brand-200"
              disabled={!canPredict}
              tooltip={canPredict
                ? 'Analiza con IA cómo funcionará tu post antes de publicarlo.'
                : 'Añade un texto o media para poder analizar.'}
              onClick={onOpenAiModal}
            />
          </div>
        </div>
      )}

      {!showCopy && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500">
          Has elegido solo stories — esta publicación no llevará texto.
        </div>
      )}

      {extras}

      {/* Schedule */}
      <div>
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <label className="label mb-0 flex items-center gap-2">
            <CalendarIcon size={14} /> ¿Cuándo quieres publicarlo?
          </label>
          <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs font-medium">
            <button
              onClick={() => setPublishMode('now')}
              className={cn(
                'px-3 py-1 rounded-md transition',
                publishMode === 'now' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
              )}
            >
              Publicar ahora
            </button>
            <button
              onClick={() => setPublishMode('scheduled')}
              className={cn(
                'px-3 py-1 rounded-md transition inline-flex items-center gap-1',
                publishMode === 'scheduled' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
              )}
            >
              <Clock size={12} /> Programar
            </button>
            <button
              onClick={() => setPublishMode('queue')}
              className={cn(
                'px-3 py-1 rounded-md transition inline-flex items-center gap-1',
                publishMode === 'queue' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
              )}
            >
              <Zap size={12} /> Cola
            </button>
          </div>
        </div>

        {publishMode === 'scheduled' && (
          <>
            <DateTimePicker value={scheduleAt} onChange={setScheduleAt} tz={tz} />
            <BestTimeSuggestions
              accountIds={targets.map((t) => t.accountId)}
              tz={tz}
              value={scheduleAt}
              onPick={setScheduleAt}
            />
          </>
        )}

        {publishMode === 'queue' && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 flex items-center gap-3 flex-wrap">
            <Zap size={16} className="text-brand-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500">Próximo hueco disponible</div>
              <div className="text-sm font-medium text-slate-900 truncate">
                {nextSlotLoading || !nextSlotIso
                  ? 'Calculando…'
                  : `${new Date(nextSlotIso).toLocaleString('es-ES', { dateStyle: 'full', timeStyle: 'short', timeZone: tz })}`}
              </div>
              <div className="text-[11px] text-slate-400">Hora de {timezoneLabel(tz)}</div>
            </div>
            <button
              type="button"
              onClick={onOpenQueueModal}
              className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 font-medium hover:border-brand-300 hover:text-brand-600 transition inline-flex items-center gap-1"
            >
              <CalendarIcon size={12} /> Editar cola
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-500">
          {selectedMediaCount} archivo(s) · {targets.length} cuenta(s)
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isEditing && (
            <button
              type="button"
              onClick={onRequestApproval}
              className="btn-secondary gap-2"
              disabled={accounts.length === 0 || publishing || requestingApproval}
              title="Genera un enlace para que tu cliente revise y apruebe esta publicación antes de programarla."
            >
              <UserCheck size={14} />
              {requestingApproval ? 'Creando…' : 'Pedir aprobación'}
            </button>
          )}
          <button
            onClick={onPublish}
            className="btn-primary gap-2"
            disabled={accounts.length === 0 || publishing || requestingApproval}
          >
            <Send size={14} />
            {publishing
              ? isEditing ? 'Guardando…' : 'Creando…'
              : isEditing
                ? 'Guardar cambios'
                : publishMode === 'scheduled'
                  ? 'Programar publicación'
                  : publishMode === 'queue'
                    ? 'Añadir a la cola'
                    : 'Publicar ahora'}
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * "Mejores horas": the next few best times for the selected accounts, from
 * their own history. One click sets the picker.
 */
function BestTimeSuggestions(props: {
  accountIds: string[];
  tz: string;
  value: string;
  onPick: (v: string) => void;
}) {
  const { data } = useBestTimes(props.accountIds);
  const options = useMemo(() => (data ? upcomingBestTimes(data, props.tz) : []), [data, props.tz]);
  if (!data || options.length === 0) return null;

  const label = (v: string) =>
    new Date(zonedInputToIso(v, props.tz)).toLocaleString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: props.tz,
    });

  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
      <span
        className="inline-flex items-center gap-1 text-xs text-slate-500"
        title={bestTimesSourceNote(data)}
      >
        <Sparkles size={12} className="text-brand-600" /> Mejores horas:
      </span>
      {options.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => props.onPick(v)}
          className={cn(
            'text-xs px-2.5 py-1 rounded-full border font-medium transition',
            v === props.value
              ? 'bg-brand-600 border-brand-600 text-white'
              : 'border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300',
          )}
        >
          {label(v)}
        </button>
      ))}
    </div>
  );
}

/** Modal shown after creating an approval request — surfaces the shareable link. */
function ApprovalLinkModal({
  link,
  reviewersNotified,
  onClose,
}: {
  link: string;
  reviewersNotified: number;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the input is selectable as a fallback */
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
            <UserCheck size={20} className="text-emerald-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">Solicitud de aprobación creada</h2>
            <p className="mt-1 text-sm text-slate-600">
              Comparte este enlace con tu cliente. Podrá revisar la publicación y aprobarla, pedir
              cambios o rechazarla. La publicación no saldrá hasta que la apruebe.
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <input
            readOnly
            value={link}
            onFocus={(e) => e.currentTarget.select()}
            className="input flex-1 text-sm font-mono text-slate-700"
          />
          <button type="button" onClick={copyLink} className="btn-primary gap-2 shrink-0">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>

        {reviewersNotified > 0 && (
          <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <CheckCircle2 size={15} className="shrink-0" />
            Enviado automáticamente a {reviewersNotified} revisor{reviewersNotified === 1 ? '' : 'es'} por email.
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <Link
            to="/dashboard/approvals"
            className="text-sm font-medium text-brand-600 hover:text-brand-700 inline-flex items-center gap-1"
          >
            Ver aprobaciones <ArrowRight size={14} />
          </Link>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Custom calendar + time picker — trigger button opens a dropdown calendar. */
/**
 * Editable two-digit time field (hours or minutes). Lets the user type the
 * value directly while keeping the up/down arrows working. Local string state
 * allows intermediate input (e.g. typing "1" before "17"); it commits a valid
 * number on every change and on blur normalises/pads. Arrow Up/Down step.
 */
function TimeInput({
  value,
  max,
  onCommit,
  onStep,
  ariaLabel,
}: {
  value: number;
  max: number;
  onCommit: (n: number) => void;
  onStep: (dir: 1 | -1) => void;
  ariaLabel: string;
}) {
  const [text, setText] = useState(String(value).padStart(2, '0'));
  // Re-sync when the value changes from outside (arrows, presets, calendar).
  useEffect(() => {
    setText(String(value).padStart(2, '0'));
  }, [value]);

  function handleChange(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    setText(digits);
    if (digits === '') return;
    const n = Number(digits);
    if (n <= max) onCommit(n);
  }
  function handleBlur() {
    const n = Math.min(max, Math.max(0, Number(text || '0')));
    setText(String(n).padStart(2, '0'));
    onCommit(n);
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      aria-label={ariaLabel}
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp') { e.preventDefault(); onStep(1); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); onStep(-1); }
      }}
      className="w-8 text-center text-sm font-semibold text-slate-800 tabular-nums rounded-md bg-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 caret-brand-600"
    />
  );
}

function DateTimePicker(props: { value: string; onChange: (v: string) => void; tz: string }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Parse "YYYY-MM-DDTHH:mm"
  const parsed = useMemo(() => {
    const [datePart, timePart] = (props.value ?? '').split('T');
    const [y, m, d] = (datePart ?? '').split('-').map(Number);
    const [h, min]  = (timePart ?? '00:00').split(':').map(Number);
    return { year: y || new Date().getFullYear(), month: (m || 1) - 1, day: d || 1, hour: h || 0, minute: min || 0 };
  }, [props.value]);

  const [viewYear,  setViewYear]  = useState(parsed.year);
  const [viewMonth, setViewMonth] = useState(parsed.month);

  // Keep view in sync when value changes externally
  useEffect(() => {
    setViewYear(parsed.year);
    setViewMonth(parsed.month);
  }, [parsed.year, parsed.month]);

  function emit(y: number, mo: number, d: number, h: number, min: number) {
    const pad = (n: number) => String(n).padStart(2, '0');
    props.onChange(`${y}-${pad(mo + 1)}-${pad(d)}T${pad(h)}:${pad(min)}`);
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(v => v - 1); setViewMonth(11); }
    else setViewMonth(v => v - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(v => v + 1); setViewMonth(0); }
    else setViewMonth(v => v + 1);
  }

  function preset(addMinutes: number) {
    const future = new Date(Date.now() + addMinutes * 60_000);
    props.onChange(isoToZonedInput(future.toISOString(), props.tz));
  }

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  // Shift so week starts on Monday (0=Mon…6=Sun)
  const startOffset = (firstDay + 6) % 7;
  const daysInMonth  = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrev   = new Date(viewYear, viewMonth, 0).getDate();

  const cells: { day: number; currentMonth: boolean }[] = [];
  for (let i = 0; i < startOffset; i++) {
    cells.push({ day: daysInPrev - startOffset + 1 + i, currentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, currentMonth: true });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ day: cells.length - daysInMonth - startOffset + 1, currentMonth: false });
  }

  const today = new Date();
  const isToday = (d: number) =>
    viewYear === today.getFullYear() && viewMonth === today.getMonth() && d === today.getDate();
  const isSelected = (d: number) =>
    parsed.year === viewYear && parsed.month === viewMonth && parsed.day === d;

  const DOW = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const MONTHS_SHORT = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

  const displayDate = `${String(parsed.day).padStart(2, '0')} ${MONTHS_SHORT[parsed.month]} ${parsed.year}`;
  const displayTime = `${String(parsed.hour).padStart(2, '0')}:${String(parsed.minute).padStart(2, '0')}`;

  return (
    <div ref={wrapperRef} className="relative">
      {/* ── Trigger button ── */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={cn(
          'w-full flex items-center gap-0 rounded-xl border text-sm transition overflow-hidden',
          open
            ? 'border-brand-400 ring-2 ring-brand-100'
            : 'border-slate-200 hover:border-brand-300',
        )}
      >
        {/* date + time text */}
        <span className="flex-1 flex items-center gap-2 px-3 py-2.5 text-left">
          <span className={cn('font-medium', open ? 'text-slate-800' : 'text-slate-700')}>{displayDate}</span>
          <span className="text-slate-300">·</span>
          <span className={cn('tabular-nums font-semibold', open ? 'text-brand-600' : 'text-slate-500')}>{displayTime}</span>
        </span>
        {/* calendar icon pill */}
        <span className={cn(
          'flex items-center justify-center self-stretch px-3 border-l transition',
          open ? 'bg-brand-500 border-brand-400' : 'bg-slate-50 border-slate-200',
        )}>
          <CalendarIcon size={16} className={cn('shrink-0', open ? 'text-white' : 'text-slate-400')} />
        </span>
      </button>

      {/* ── Dropdown calendar ── */}
      {open && (
        <div className="absolute z-50 bottom-full mb-2 w-full min-w-[280px] rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
      {/* ── Calendar header ── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <button
          onClick={prevMonth}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition"
        >
          <ChevronLeft size={15} />
        </button>
        <span className="text-sm font-semibold text-slate-800 capitalize">
          {MONTHS[viewMonth]} de {viewYear}
        </span>
        <button
          onClick={nextMonth}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition"
        >
          <ChevronRight size={15} />
        </button>
      </div>

      {/* ── Days of week ── */}
      <div className="grid grid-cols-7 px-3 pb-1">
        {DOW.map(d => (
          <div key={d} className="text-center text-[11px] font-medium text-slate-400 py-1">{d}</div>
        ))}
      </div>

      {/* ── Calendar cells ── */}
      <div className="grid grid-cols-7 gap-y-0.5 px-3 pb-3">
        {cells.map((cell, i) => {
          const sel = cell.currentMonth && isSelected(cell.day);
          const tod = cell.currentMonth && isToday(cell.day);
          return (
            <button
              key={i}
              disabled={!cell.currentMonth}
              onClick={() => cell.currentMonth && emit(viewYear, viewMonth, cell.day, parsed.hour, parsed.minute)}
              className={cn(
                'mx-auto w-8 h-8 flex items-center justify-center rounded-full text-[13px] transition',
                !cell.currentMonth && 'text-slate-300 cursor-default',
                cell.currentMonth && !sel && !tod && 'text-slate-700 hover:bg-brand-50 hover:text-brand-600',
                tod && !sel && 'text-brand-600 font-semibold bg-brand-50',
                sel && 'bg-brand-500 text-white font-semibold shadow-sm',
              )}
            >
              {cell.day}
            </button>
          );
        })}
      </div>

      {/* ── Divider ── */}
      <div className="border-t border-slate-100 mx-3" />

      {/* ── Time selector ── */}
      <div className="flex items-center justify-center gap-3 px-4 py-3">
        <Clock size={14} className="text-slate-400 shrink-0" />
        <div className="flex items-center gap-1.5">
          {/* Hours */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => emit(parsed.year, parsed.month, parsed.day, (parsed.hour + 1) % 24, parsed.minute)}
              className="w-7 h-6 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 transition"
            ><ChevronLeft size={13} className="-rotate-90" /></button>
            <TimeInput
              value={parsed.hour}
              max={23}
              onCommit={(h) => emit(parsed.year, parsed.month, parsed.day, h, parsed.minute)}
              onStep={(dir) => emit(parsed.year, parsed.month, parsed.day, (parsed.hour + (dir > 0 ? 1 : 23)) % 24, parsed.minute)}
              ariaLabel="Hora"
            />
            <button
              onClick={() => emit(parsed.year, parsed.month, parsed.day, (parsed.hour + 23) % 24, parsed.minute)}
              className="w-7 h-6 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 transition"
            ><ChevronLeft size={13} className="rotate-90" /></button>
          </div>
          <span className="text-slate-400 font-semibold text-sm pb-0.5">:</span>
          {/* Minutes */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => emit(parsed.year, parsed.month, parsed.day, parsed.hour, (parsed.minute + 5) % 60)}
              className="w-7 h-6 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 transition"
            ><ChevronLeft size={13} className="-rotate-90" /></button>
            <TimeInput
              value={parsed.minute}
              max={59}
              onCommit={(m) => emit(parsed.year, parsed.month, parsed.day, parsed.hour, m)}
              onStep={(dir) => emit(parsed.year, parsed.month, parsed.day, parsed.hour, (parsed.minute + (dir > 0 ? 5 : 55)) % 60)}
              ariaLabel="Minutos"
            />
            <button
              onClick={() => emit(parsed.year, parsed.month, parsed.day, parsed.hour, (parsed.minute + 55) % 60)}
              className="w-7 h-6 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 transition"
            ><ChevronLeft size={13} className="rotate-90" /></button>
          </div>
        </div>
        <span className="text-[11px] text-slate-400 ml-1">{timezoneLabel(props.tz)}</span>
      </div>

      {/* ── Quick presets + save ── */}
      <div className="border-t border-slate-100 px-3 py-2.5 flex flex-wrap items-center gap-1.5">
        {[
          { label: 'En 1 h',         mins: 60        },
          { label: 'En 3 h',         mins: 180       },
          { label: 'Mañana',         mins: 60 * 24   },
          { label: 'Próxima semana', mins: 60 * 24 * 7 },
        ].map(({ label, mins }) => (
          <button
            key={label}
            onClick={() => { preset(mins); setOpen(false); }}
            className="text-[11px] px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 text-slate-600 transition"
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => setOpen(false)}
          className="ml-auto flex items-center gap-1 text-[11px] px-3 py-1 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-medium transition"
        >
          <Save size={11} strokeWidth={2.5} />
          Guardar
        </button>
      </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   RIGHT — Preview panel
   ─────────────────────────────────────────────────────────── */
function PreviewPanel(props: {
  targets: Target[];
  accounts: ConnectedAccount[];
  activeIdx: number;
  setActiveIdx: (i: number) => void;
  media: MediaItem[];
  copy: string;
  currentAccount: ConnectedAccount | null;
  currentTarget: Target | undefined;
}) {
  const {
    targets,
    accounts,
    activeIdx,
    setActiveIdx,
    media,
    copy,
    currentAccount,
    currentTarget,
  } = props;

  // Preview falls back to the global `copy` when the active target has no
  // per-network override. (The editor for these overrides lives in the
  // Composer, not here, so the user sees "what they're editing" in one place.)
  const hasOverride = currentTarget?.caption !== null && currentTarget?.caption !== undefined;
  const captionForPreview = hasOverride ? (currentTarget!.caption ?? '') : copy;

  return (
    <aside className="lg:col-span-2 xl:col-span-1">
      <div className="card p-0 overflow-hidden lg:sticky lg:top-6">
        <div className="px-4 py-3 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900 text-sm">Preview</h2>
          <p className="text-xs text-slate-500 mt-0.5">Así se verá en cada red.</p>
        </div>

        {targets.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">
            Selecciona una red social para ver cómo quedará.
          </div>
        ) : (
          <>
            {/* tabs */}
            <div className="px-3 pt-3 flex flex-wrap gap-1.5 border-b border-slate-100 pb-3">
              {targets.map((t, i) => {
                const acc = accounts.find((a) => a.id === t.accountId);
                if (!acc) return null;
                const meta = PLATFORM_META[acc.platform];
                const Icon = meta.icon;
                const active = i === activeIdx;
                const customized = t.caption !== null && t.caption.trim().length > 0;
                return (
                  <button
                    key={t.accountId}
                    onClick={() => setActiveIdx(i)}
                    className={cn(
                      'inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border transition relative',
                      active
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300',
                    )}
                    title={`${meta.label} · ${KIND_LABEL[t.kind]}${customized ? ' · texto personalizado' : ''}`}
                  >
                    <Icon size={12} color={meta.color} />
                    <span className="truncate max-w-[80px]">{acc.username ?? acc.displayName ?? meta.label}</span>
                    <span className="text-slate-400">·</span>
                    <span>{KIND_LABEL[t.kind]}</span>
                    {customized && (
                      <span
                        aria-hidden
                        className="ml-0.5 h-1.5 w-1.5 rounded-full bg-amber-500"
                        title="Texto personalizado para esta red"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* preview frame */}
            <div className="p-4 bg-slate-50 flex justify-center">
              {currentAccount && currentTarget ? (
                <PlatformPreview
                  account={currentAccount}
                  kind={currentTarget.kind}
                  media={media}
                  caption={captionForPreview}
                />
              ) : null}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────
   Success banner (auto-dismisses after 30 s via parent useEffect)
   ─────────────────────────────────────────────────────────── */
function SuccessBanner({
  okMsg,
  tz,
  onClose,
}: {
  okMsg: OkMsg;
  tz: string;
  onClose: () => void;
}) {
  const accountLabel = (t: OkMsgTarget): string => {
    const name = t.displayName || t.username || '';
    const platformLabel = PLATFORM_META[t.platform]?.label ?? '';
    return name ? `${name} (${platformLabel})` : platformLabel;
  };
  const accountsText = okMsg.targets.map(accountLabel).join(', ');

  let title = '';
  if (okMsg.mode === 'now') {
    title = `Tu publicación se está publicando en ${accountsText}.`;
  } else if (okMsg.mode === 'scheduled' && okMsg.scheduledAtIso) {
    const d = new Date(okMsg.scheduledAtIso);
    const dateStr = d.toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: tz,
    });
    const timeStr = d.toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz,
    });
    title = `Se ha programado para el ${dateStr} a las ${timeStr} en ${accountsText}.`;
  } else if (okMsg.mode === 'queue' && okMsg.scheduledAtIso) {
    const d = new Date(okMsg.scheduledAtIso);
    const dateStr = d.toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: tz,
    });
    const timeStr = d.toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz,
    });
    title = `Añadida a la cola para el ${dateStr} a las ${timeStr} en ${accountsText}.`;
  } else {
    title = `Publicación creada en ${accountsText}.`;
  }

  return (
    <div className="mt-6 card border-emerald-200 bg-emerald-50 flex items-start gap-3">
      <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-emerald-900 font-medium">{title}</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {okMsg.targets.slice(0, 6).map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 bg-white/70 border border-emerald-200 rounded-full px-2 py-0.5">
              <AccountAvatar
                account={{
                  platform: t.platform,
                  username: t.username,
                  displayName: t.displayName,
                  profilePhotoUrl: t.profilePhotoUrl,
                }}
                size={16}
              />
              <span className="text-xs text-emerald-900">
                {t.displayName || t.username || PLATFORM_META[t.platform]?.label || ''}
              </span>
            </span>
          ))}
          {okMsg.targets.length > 6 && (
            <span className="text-xs text-emerald-700">+{okMsg.targets.length - 6}</span>
          )}
        </div>
      </div>
      <Link
        to={`/dashboard/posts?tab=${okMsg.mode === 'now' ? 'publishing' : 'scheduled'}`}
        className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-900 bg-white hover:bg-emerald-100 border border-emerald-200 rounded-lg px-3 py-1.5 transition whitespace-nowrap"
      >
        Míralo aquí
        <ArrowRight size={14} />
      </Link>
      <button
        onClick={onClose}
        className="shrink-0 text-emerald-700 hover:text-emerald-900 transition"
        aria-label="Cerrar"
      >
        <X size={16} />
      </button>
    </div>
  );
}

/** Next full hour in the user's timezone, as a datetime-local value. */
function defaultScheduleValue(tz: string): string {
  const z = nowZoned(tz);
  z.setUTCHours(z.getUTCHours() + 1, 0, 0, 0);
  return zonedDateToInput(z);
}

/** PUT a file to a presigned URL and report progress. */
function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Upload network error'));
    xhr.send(file);
  });
}
